import { useState, useEffect } from "react";
import { toast } from "sonner";
import { customCategoriesService } from "../../services/customCategories";
import { storesService } from "../../services/stores";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import { List, Search, Filter } from "lucide-react";

export default function AdminResellerCategories() {
    const [categories, setCategories] = useState([]);
    const [stores, setStores] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filterStore, setFilterStore] = useState("all");
    const [searchQuery, setSearchQuery] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");

    useEffect(() => {
        const timer = setTimeout(() => setDebouncedSearch(searchQuery), 500);
        return () => clearTimeout(timer);
    }, [searchQuery]);

    useEffect(() => {
        fetchStores();
    }, []);

    useEffect(() => {
        fetchCategories();
    }, [filterStore, debouncedSearch]);

    const fetchStores = async () => {
        try {
            const data = await storesService.getAll();
            setStores(data);
        } catch (error) {
            console.error("Failed to fetch stores", error);
        }
    };

    const fetchCategories = async () => {
        setLoading(true);
        try {
            const params = {};
            if (filterStore !== "all") params.store_id = filterStore;
            if (debouncedSearch) params.search = debouncedSearch;
            params.sort_by = 'store_name';

            const data = await customCategoriesService.getAll(params);
            const categoriesData = data && Array.isArray(data.data) ? data.data : (Array.isArray(data) ? data : []);
            setCategories(categoriesData);
        } catch (error) {
            console.error("Failed to fetch reseller categories", error);
            toast.error("Failed to fetch categories");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="space-y-6 animate-fade-in" data-testid="admin-reseller-categories">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div>
                    <h2 className="text-2xl font-bold text-slate-900">
                        Reseller Categories
                    </h2>
                    <p className="text-slate-500">View categories from all resellers</p>
                </div>
                <div className="flex items-center gap-3">
                    <div className="relative w-64">
                        <Search className="absolute left-2 top-2.5 h-4 w-4 text-slate-500" />
                        <Input
                            placeholder="Search categories..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="pl-8"
                        />
                    </div>
                    <Select value={filterStore} onValueChange={setFilterStore}>
                        <SelectTrigger className="w-48">
                            <Filter className="w-4 h-4 mr-2 text-slate-500" />
                            <SelectValue placeholder="Filter by Store" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All Stores</SelectItem>
                            {stores.map((store) => (
                                <SelectItem key={store.store_id} value={store.store_id}>
                                    {store.business_name || store.store_name || "Unknown Store"}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
            </div>

            <div className="card-elevated overflow-hidden">
                <Table>
                    <TableHeader>
                        <TableRow className="bg-slate-50">
                            <TableHead className="font-semibold">Category</TableHead>
                            <TableHead className="font-semibold">Store</TableHead>
                            <TableHead className="font-semibold">Status</TableHead>
                            <TableHead className="font-semibold text-right">Created</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {loading ? (
                            <TableRow>
                                <TableCell colSpan={4} className="h-32 text-center">
                                    <div className="flex items-center justify-center">
                                        <div className="w-6 h-6 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
                                    </div>
                                </TableCell>
                            </TableRow>
                        ) : !Array.isArray(categories) || categories.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={4} className="text-center py-8 text-slate-500">
                                    No categories found.
                                </TableCell>
                            </TableRow>
                        ) : (
                            categories.map((category) => (
                                <TableRow key={category._id} className="table-row">
                                    <TableCell>
                                        <div className="flex items-center gap-3">
                                            <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                                                <List className="w-5 h-5 text-slate-400" />
                                            </div>
                                            <div>
                                                <span className="font-medium text-slate-900">{category.name}</span>
                                                {category.description && (
                                                    <p className="text-xs text-slate-500 truncate max-w-[200px]">{category.description}</p>
                                                )}
                                            </div>
                                        </div>
                                    </TableCell>
                                    <TableCell>
                                        <span className="font-medium text-slate-700">
                                            {category.store_name || "Unknown"}
                                        </span>
                                    </TableCell>
                                    <TableCell>
                                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${category.is_active !== false ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600'}`}>
                                            {category.is_active !== false ? 'Active' : 'Inactive'}
                                        </span>
                                    </TableCell>
                                    <TableCell className="text-right text-slate-500 text-sm">
                                        {new Date(category.created_at || category.createdAt).toLocaleDateString()}
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </div>
        </div>
    );
}
